import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";

import Slider from "react-slick";

import { InteractionType } from "@/types/ga4";

export type ResultsTrackingPayload = {
  index: number;
  interaction: InteractionType;
};

/* Set by embla-based sliders. When present, controls drive it instead of the react-slick instance. */
export type ResultsSliderApi = {
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
};

export type ResultsContextValue = {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  autoplay: boolean;
  setAutoplay: (value: boolean) => void;
  autoplaySpeed: number;
  setAutoplaySpeed: (value: number) => void;
  slidesCount: number;
  resultSliderRef: Slider | null;
  setResultSliderRef: Dispatch<SetStateAction<Slider | null>>;
  thumbnailSliderRef: Slider | null;
  setThumbnailSliderRef: Dispatch<SetStateAction<Slider | null>>;
  sliderApi: ResultsSliderApi | null;
  setSliderApi: Dispatch<SetStateAction<ResultsSliderApi | null>>;
  goToSlide: (index: number) => void;
  track?: (payload: ResultsTrackingPayload) => void;
};

const ResultsContext = createContext<ResultsContextValue | null>(null);

export const useResultsContext = () => {
  const ctx = useContext(ResultsContext);
  if (!ctx) {
    throw new Error("useResultsContext must be used within <Results> (ResultsProvider)");
  }
  return ctx;
};

type ResultsProviderProps = {
  children: ReactNode;
  slidesCount: number;
  autoplay?: boolean;
  track?: (payload: ResultsTrackingPayload) => void;
};

export const ResultsProvider = ({
  children,
  slidesCount,
  autoplay = true,
  track,
}: ResultsProviderProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoplay, setAutoplay] = useState(autoplay);
  const [autoplaySpeed, setAutoplaySpeed] = useState(10000);
  const [resultSliderRef, setResultSliderRef] = useState<Slider | null>(null);
  const [thumbnailSliderRef, setThumbnailSliderRef] = useState<Slider | null>(null);
  const [sliderApi, setSliderApi] = useState<ResultsSliderApi | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (sliderApi) {
        sliderApi.scrollTo(index);
      } else if (resultSliderRef?.slickGoTo) {
        resultSliderRef.slickGoTo(index);
      }
    },
    [sliderApi, resultSliderRef]
  );

  const value = useMemo(
    () => ({
      activeIndex,
      setActiveIndex,
      autoplay: isAutoplay,
      setAutoplay,
      autoplaySpeed,
      setAutoplaySpeed,
      slidesCount,
      resultSliderRef,
      setResultSliderRef,
      thumbnailSliderRef,
      setThumbnailSliderRef,
      sliderApi,
      setSliderApi,
      goToSlide,
      track,
    }),
    [
      activeIndex,
      isAutoplay,
      autoplaySpeed,
      slidesCount,
      resultSliderRef,
      thumbnailSliderRef,
      sliderApi,
      goToSlide,
      track,
    ]
  );

  return <ResultsContext.Provider value={value}>{children}</ResultsContext.Provider>;
};
